import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ALL_DIMENSION_ORDERS,
  ALL_DIMENSION_UNITS,
  ALL_WEIGHT_UNITS,
  DataConvention,
  DEFAULT_DATA_CONVENTION,
  DimensionAxis,
  DimensionOrder,
  DimensionUnit,
  WeightUnit,
  axesFor,
  dimensionOrderLabel,
} from 'src/app/models/organizations/data-convention';

type DataConventionFormGroup = FormGroup<{
  dimensionOrder: FormControl<DimensionOrder>;
  dimensionUnit: FormControl<DimensionUnit>;
  weightUnit: FormControl<WeightUnit>;
}>;

interface SampleValuesMm {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly depthMm: number;
  readonly weightKg: number;
}

const SAMPLE_VALUES: SampleValuesMm = {
  widthMm: 100,
  heightMm: 200,
  depthMm: 300,
  weightKg: 1.5,
};

const DIMENSION_UNIT_DIVISORS: Record<DimensionUnit, number> = {
  mm: 1,
  m: 1000,
};

const WEIGHT_UNIT_FACTORS: Record<WeightUnit, number> = {
  kg: 1,
  g: 1000,
};

const SAMPLE_MM_FOR_AXIS: Record<DimensionAxis, number> = {
  width: SAMPLE_VALUES.widthMm,
  height: SAMPLE_VALUES.heightMm,
  depth: SAMPLE_VALUES.depthMm,
};

export const roundForDisplay = (value: number): number =>
  Math.round(value * 100000) / 100000;

export const convertDimension = (valueMm: number, unit: DimensionUnit): number =>
  roundForDisplay(valueMm / DIMENSION_UNIT_DIVISORS[unit]);

export const convertWeight = (valueKg: number, unit: WeightUnit): number =>
  roundForDisplay(valueKg * WEIGHT_UNIT_FACTORS[unit]);

export const buildDimensionsPreview = (
  convention: DataConvention,
): Record<string, number | string> => {
  const dimensionRecord: Record<string, number | string> = axesFor(convention.dimensionOrder).reduce(
    (acc: Record<string, number | string>, axis: DimensionAxis) => ({
      ...acc,
      [axis]: convertDimension(SAMPLE_MM_FOR_AXIS[axis], convention.dimensionUnit),
    }),
    {},
  );
  return { ...dimensionRecord, unit: convention.dimensionUnit };
};

export const buildWeightPreview = (
  convention: DataConvention,
): Record<string, number | string> => ({
  value: convertWeight(SAMPLE_VALUES.weightKg, convention.weightUnit),
  unit: convention.weightUnit,
});

export const renderPreviewJson = (convention: DataConvention): string =>
  JSON.stringify(
    {
      weight: buildWeightPreview(convention),
      dimensions: buildDimensionsPreview(convention),
    },
    null,
    2,
  );

@Component({
  selector: 'app-data-convention-update',
  templateUrl: './data-convention-update.component.html',
  styleUrls: ['./data-convention-update.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class DataConventionUpdateComponent implements OnInit {
  @Input() dataConvention: DataConvention = { ...DEFAULT_DATA_CONVENTION };
  @Output() dataConventionChange = new EventEmitter<DataConvention>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  readonly allDimensionOrders: readonly DimensionOrder[] = ALL_DIMENSION_ORDERS;
  readonly allDimensionUnits: readonly DimensionUnit[] = ALL_DIMENSION_UNITS;
  readonly allWeightUnits: readonly WeightUnit[] = ALL_WEIGHT_UNITS;

  form: DataConventionFormGroup = this.buildForm(DEFAULT_DATA_CONVENTION);
  previewJson: string = renderPreviewJson(DEFAULT_DATA_CONVENTION);

  ngOnInit(): void {
    // Hydrate
    this.hydrateForm(this.dataConvention);
    // Refresh preview from hydrated state
    this.previewJson = renderPreviewJson(this.currentConvention());
    // Subscribe
    this.subscribeToValueChanges();
  }

  orderLabel(order: DimensionOrder): string {
    return dimensionOrderLabel(order);
  }

  private buildForm(seed: DataConvention): DataConventionFormGroup {
    return this.fb.nonNullable.group({
      dimensionOrder: this.fb.nonNullable.control<DimensionOrder>(seed.dimensionOrder),
      dimensionUnit: this.fb.nonNullable.control<DimensionUnit>(seed.dimensionUnit),
      weightUnit: this.fb.nonNullable.control<WeightUnit>(seed.weightUnit),
    });
  }

  private hydrateForm(convention: DataConvention): void {
    this.form.patchValue(
      {
        dimensionOrder: convention.dimensionOrder,
        dimensionUnit: convention.dimensionUnit,
        weightUnit: convention.weightUnit,
      },
      { emitEvent: false },
    );
  }

  private subscribeToValueChanges(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onFormChanged());
  }

  private onFormChanged(): void {
    const convention: DataConvention = this.currentConvention();
    this.previewJson = renderPreviewJson(convention);
    this.dataConventionChange.emit(convention);
  }

  private currentConvention(): DataConvention {
    return {
      dimensionOrder: this.form.controls.dimensionOrder.value,
      dimensionUnit: this.form.controls.dimensionUnit.value,
      weightUnit: this.form.controls.weightUnit.value,
    };
  }
}
